import 'react-toastify/dist/ReactToastify.css';
import React from 'react';
import {
  toast,
  ToastContainer,
  Slide,
  type ToastOptions,
} from 'react-toastify';
import cn from '../utils/cn';
import { zIndex } from '../../z-index';

// The UI kit's snackbar: one navy toast for every type; the type shows in the
// colour of its round icon.
const snackClass =
  'relative mb-2 flex items-center gap-3 overflow-hidden rounded-xl bg-k-h px-4 py-3.5 text-sm text-white shadow-[0_10px_30px_rgba(27,44,73,.2)] cursor-pointer';

const typeIcons = {
  default: 'fa-info',
  info: 'fa-info',
  success: 'fa-check',
  warning: 'fa-exclamation',
  error: 'fa-exclamation',
};

const iconTones = {
  default: 'bg-navy-500 text-white',
  info: 'bg-navy-500 text-white',
  success: 'bg-green-600 text-white',
  warning: 'bg-k-orange text-k-h',
  error: 'bg-red-600 text-white',
};

const variantOf = (type?: string) =>
  (type && type in typeIcons ? type : 'default') as keyof typeof typeIcons;

export const notify = (
  message: React.ReactNode,
  options: ToastOptions = {},
) => {
  const type = variantOf(options.type ?? 'success');
  return toast(
    <>
      <span
        className={cn(
          'flex size-7 flex-none items-center justify-center rounded-full text-xs',
          iconTones[type],
        )}
      >
        <i className={cn('fa-solid', typeIcons[type])} />
      </span>
      <div className="min-w-0 flex-1">{message}</div>
    </>,
    { ...options, type },
  );
};

export const notifyError = (
  message: React.ReactNode,
  options: ToastOptions = {},
) => notify(message, { type: 'error', ...options });

// toast() is a module-level singleton, so no context is needed; the hook is
// kept so the 200+ `useSnack()` call sites keep working unchanged.
const snackApi = { notify, notifyError };
export const useSnack = () => snackApi;

const CloseButton = ({ closeToast }: { closeToast?: () => void }) => (
  <button
    type="button"
    aria-label="close"
    onClick={closeToast}
    className="flex size-7 flex-none items-center justify-center self-center border-none bg-transparent p-0 text-base text-navy-200 cursor-pointer hover:text-white"
  >
    <i className="fa-light fa-xmark" />
  </button>
);

const KoobSnackProvider = ({ children }: { children?: React.ReactNode }) => (
  <>
    {children}
    <ToastContainer
      position="bottom-center"
      transition={Slide}
      // Toasts are the top tier of the scale (packages/ui/z-index.js): they
      // must never be covered, including by a modal or its popovers.
      // width overrides the 320px default from ReactToastify.css (the
      // container centers itself with translateX(-50%))
      // min() so the inline width can't defeat the mobile (<480px)
      // full-width media query in ReactToastify.css
      style={{ width: 'min(420px, 100vw)', zIndex: zIndex.snack }}
      toastClassName={() => snackClass}
      bodyClassName={() => 'flex items-center gap-3 min-w-0 flex-1'}
      progressClassName={(ctx) =>
        cn(ctx?.defaultClassName, '!bg-white !opacity-20')
      }
      closeButton={CloseButton}
    />
  </>
);

export default KoobSnackProvider;
